import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import React, { useContext } from 'react';
import { UserRoles, IItemRestaurant } from 'types';
import { posSystemTypes } from 'appConstants';
import classNames from 'classnames';
import { FullInfoBtn } from '../../../../FullInfoBtn/FullInfoBtn';
import RestaurantManagersScrollView from './components/RestaurantManagersList';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { RestaurantsContext, IRestaurantsContext } from '../../../../../../../restaurantsState/restaurantsContext';
import style from './Info.module.scss';

interface IInfo {
	isOpenInfo: boolean;
	data: IItemRestaurant;
}

const Info: React.FC<IInfo> = ({ isOpenInfo, data }) => {
	const { setFullInfoData } = useContext<IRestaurantsContext>(RestaurantsContext);
	const containerClasses = classNames(style.container, { [style.visible]: isOpenInfo });
	const hasExternalInfoAccess = useRoleAccess([UserRoles.SUPER_USER]);
	const hasPushesAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const posSystemName = posSystemTypes.find((el) => el.posSystemID === data.posSystemId)?.posSystemName || null;

	const handleOpenFullInfo = () => {
		setFullInfoData(data.id);
	};

	const handleOpenSoftwareUpdates = () => {
		setFullInfoData(data.id, 'softwareForm');
	};

	return (
		<div className={containerClasses} data-test="Info">
			<div className={style.wrapper}>
				<div className={style.infoContainer}>
					<div className={style.block}>
						<Text
							className={style.blockName}
							translationNameSpace="listing"
							translationKey="addressLabel"
							theme="neutralLight"
						/>
						<Text className={style.blockValue} translationKey={data.address} withTooltip={true} />
						<Text
							className={style.blockName}
							translationNameSpace="listing"
							translationKey="customerLabel"
							theme="neutralLight"
						/>
						<Text className={style.blockValue} translationKey={data.customer.name} withTooltip={true} />
						<Text
							className={style.blockName}
							translationNameSpace="listing"
							translationKey="cityLabel"
							theme="neutralLight"
						/>
						<Text className={style.blockValue} translationKey={data.city.name} withTooltip={true} />
						<Text
							className={style.blockName}
							translationNameSpace="listing"
							translationKey="countryLabel"
							theme="neutralLight"
						/>
						<Text className={style.blockValue} translationKey={data.country.name} withTooltip={true} />
					</div>
					<div className={style.block}>
						<Text
							className={style.blockName}
							translationNameSpace="listing"
							translationKey="currencyLabel"
							theme="neutralLight"
						/>
						<Text className={style.blockValue} translationKey={data.isoCurrencySymbol} withTooltip={true} />
						{hasExternalInfoAccess && (
							<>
								<Text
									className={style.blockName}
									translationNameSpace="listing"
									translationKey="posLabel"
									theme="neutralLight"
								/>
								<Text className={style.blockValue} translationKey={posSystemName} withTooltip={true} />
							</>
						)}
					</div>
					<div className={style.block}>
						{hasExternalInfoAccess && (
							<>
								<Text
									className={style.blockName}
									translationNameSpace="listing"
									translationKey="restaurantManagerLabel"
									theme="neutralLight"
								/>
								<div className={style.blockValue}>
									<RestaurantManagersScrollView data={data.restaurantManagers} />
								</div>
							</>
						)}
					</div>
				</div>
				<div className={style.fullInfoBtn}>
					{hasPushesAccess && (
						<Button className={style.pushesButton} onClick={handleOpenSoftwareUpdates}>
							<Text translationNameSpace="form" translationKey="pushesButton" asFragment={true} />
						</Button>
					)}
					<FullInfoBtn onClick={handleOpenFullInfo} />
				</div>
			</div>
		</div>
	);
};

export default React.memo(Info);
